import { useEffect, useState } from 'react';

/**
 * Scroll-spy hook: returns the id of the currently visible section
 * given a list of element ids on the home page.
 * Returns '' when none are intersecting or on non-home routes.
 */
export function useActiveSection(ids, enabled = true) {
  const [activeId, setActiveId] = useState('');

  useEffect(() => {
    if (!enabled || !ids?.length) return undefined;

    const elements = ids
      .map((id) => document.getElementById(id))
      .filter(Boolean);

    if (!elements.length) return undefined;

    const visibility = new Map();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          visibility.set(entry.target.id, entry.intersectionRatio);
        });
        let bestId = '';
        let bestRatio = 0;
        for (const [id, ratio] of visibility.entries()) {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestId = id;
          }
        }
        setActiveId(bestId);
      },
      {
        // Top offset accounts for sticky navbar (~70px).
        rootMargin: '-72px 0px -45% 0px',
        threshold: [0.1, 0.25, 0.5, 0.75]
      }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [ids, enabled]);

  return activeId;
}
